import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TagIcon, InfoIcon, DownloadIcon, PackageIcon, PackageCheckIcon, PrinterIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { LinkPackageModal, ProductPreview } from 'components'
import { BuyLabelConfirmModal, printLabel } from 'pages/internal/OrderDetailPage/components'

import { useSnackbar } from 'contexts'
import {
  useLinkPackagesToOrder,
  useCreateShipment,
  useBuyShipmentLabel,
  useFulfillShipment,
  useOrderDocumentDownload,
} from 'hooks/api/useShopifyOrdersQuery'
import type { ShopifyOrder } from 'store/orders/ordersStore'

/**
 * VRG-512 (§71) — the row/bulk actions for an orders table, and the modals they
 * drive, extracted so the standalone `/internal/orders` page and the company
 * detail page's Orders tab render the SAME actions with the SAME gating.
 *
 * Shared here rather than left per-surface because gating is the part that drifts:
 * "Buy Label" is single-product-only, "Fulfill" needs a purchased label, "Assign"
 * needs an unfulfilled order. Restating those conditions in a second file is how a
 * tab ends up offering an action the page correctly withholds (or, as the Orders
 * tab did before this, offering none at all).
 *
 * A HOOK, not a constant: the actions close over `navigate`, the referrer (§42),
 * the snackbar and five mutations. `ROLE_MEMBER_COLUMNS` (RolePageSkeleton) is a
 * plain array precisely because it feeds a skeleton, which has no handlers — it is
 * not the model for anything with behaviour.
 *
 * `modals` MUST be rendered by the consumer; the returned actions are inert without
 * it (the assign step-through and the buy-label confirm both live there).
 */

/**
 * A single-product order's shipment is 1:1 with the order — the box IS the
 * product's own package, no grouping choice — so `shipmentsSummary[0]` is "the"
 * shipment once one exists. Exported because the shared `Shipping Label` column
 * needs the same resolution the kebab's Print item uses.
 */
export const getSingleShipment = (row: ShopifyOrder) =>
  (row.lineItems?.length ?? 0) === 1 ? row.shipmentsSummary?.[0] : undefined

export const canBuyLabel = (row: ShopifyOrder) =>
  (row.lineItems?.length ?? 0) === 1 &&
  (row.assignedLineItemCount ?? 0) > 0 &&
  getSingleShipment(row)?.labelStatus !== 'purchased'

export const canFulfill = (row: ShopifyOrder) => {
  const shipment = getSingleShipment(row)
  return shipment?.labelStatus === 'purchased' && shipment.status === 'ready'
}

/**
 * Printable at any point once a label is purchased (not gated by fulfillment
 * status like Fulfill — you can reprint a shipped order's label).
 */
export const canPrintLabel = (row: ShopifyOrder) => {
  const shipment = getSingleShipment(row)
  return shipment?.labelStatus === 'purchased' && !!shipment.labelUrl
}

interface UseOrderRowActionsParams {
  /** Router state from `useReferrerState()` — handed to the order drill-in so its Header offers a way back (§42). */
  referrer?: { backTo: string; backLabel: string }
  /** Refetches the list this table is showing, after an assign/label/fulfill mutation. */
  refetch: () => void
}

export const useOrderRowActions = ({ referrer, refetch }: UseOrderRowActionsParams) => {
  const navigate = useNavigate()
  const { open: notify } = useSnackbar()

  // Per-product Assign step-through. Kebab = 1 order; bulk = N selected orders.
  // The modal walks each order's PRODUCTS one at a time — a package-scan per
  // product, with a product preview above the scanner (mirrors the Packages-page
  // AddPackagesModal). "Next Product" advances within an order; "Next Order"
  // (bulk) jumps to the next order. Single-product order = no next button.
  const [assignQueue, setAssignQueue] = useState<ShopifyOrder[]>([])
  const [assignOrderIndex, setAssignOrderIndex] = useState(0)
  const [assignProductIndex, setAssignProductIndex] = useState(0)

  // Kebab "Buy Label" — single-product orders only. The order awaiting confirm;
  // null = modal closed. Money action — always confirmed first.
  const [buyLabelOrder, setBuyLabelOrder] = useState<ShopifyOrder | null>(null)

  // Registered so the mutation is mounted while the modal is open (the modal
  // itself fires it); mirrors the original page's wiring.
  useLinkPackagesToOrder()
  const createShipment = useCreateShipment()
  const buyShipmentLabel = useBuyShipmentLabel()
  const fulfillShipment = useFulfillShipment()
  const downloadOrderDocument = useOrderDocumentDownload()

  const handleDownloadInvoice = (order: ShopifyOrder) => {
    downloadOrderDocument.mutate(
      { orderId: order.id, orderName: order.name },
      {
        onError: () =>
          notify({ type: 'error', text: 'Unable to download this order. Please try again.' }),
      },
    )
  }

  // --- Per-product Assign step-through (kebab = [row]; bulk = selectedRows) ---
  const startAssign = (rows: ShopifyOrder[]) => {
    // Only unfulfilled orders with products can take package assignments.
    const eligible = rows.filter(
      r => (!r.fulfillmentStatus || r.fulfillmentStatus === 'unfulfilled') && (r.lineItems?.length ?? 0) > 0,
    )
    if (!eligible.length) return
    setAssignQueue(eligible)
    setAssignOrderIndex(0)
    setAssignProductIndex(0)
  }
  const closeAssign = () => {
    setAssignQueue([])
    setAssignOrderIndex(0)
    setAssignProductIndex(0)
  }

  const assignOrder = assignQueue[assignOrderIndex] ?? null
  const assignProducts = assignOrder?.lineItems ?? []
  const assignProduct = assignProducts[assignProductIndex] ?? null
  const isBulkAssign = assignQueue.length > 1
  const hasNextProduct = assignProductIndex < assignProducts.length - 1
  const hasNextOrder = assignOrderIndex < assignQueue.length - 1
  const hasPrevProduct = assignProductIndex > 0

  // After a link (or "Next Product"): advance within the order → next order → close.
  const nextProduct = () => {
    if (hasNextProduct) setAssignProductIndex(i => i + 1)
    else if (hasNextOrder) {
      setAssignOrderIndex(i => i + 1)
      setAssignProductIndex(0)
    } else closeAssign()
  }
  // "Next Order" (bulk): skip the rest of this order, jump to the next one.
  const nextOrder = () => {
    if (hasNextOrder) {
      setAssignOrderIndex(i => i + 1)
      setAssignProductIndex(0)
    } else closeAssign()
  }
  // "Back": step to the previous product within the current order.
  const prevProduct = () => {
    if (hasPrevProduct) setAssignProductIndex(i => i - 1)
  }

  const closeBuyLabelConfirm = () => setBuyLabelOrder(null)

  const confirmBuyLabel = async () => {
    if (!buyLabelOrder) return
    const orderId = buyLabelOrder.id
    let shipmentId = getSingleShipment(buyLabelOrder)?.id
    try {
      // No shipment yet — single-product orders have no box choice, so create
      // it directly (mirrors OrderDetailPage's openCreateShipment for a lone
      // eligible product) before buying the label.
      if (!shipmentId) {
        const lineItemId = buyLabelOrder.lineItems?.[0]?.id
        if (!lineItemId) return
        const result = await createShipment.mutateAsync({ orderId, lineItemIds: [lineItemId] })
        shipmentId = result.shipmentId
      }
      // Guard rather than assert: the create call can resolve without an id, and
      // buying a label against `undefined` would fail server-side anyway. (TS cannot
      // narrow across the await above, so this is also what makes the call typecheck.)
      if (!shipmentId) return
      await buyShipmentLabel.mutateAsync({ orderId, shipmentId })
    } finally {
      setBuyLabelOrder(null)
    }
  }

  const handleFulfill = (row: ShopifyOrder) => {
    const shipment = getSingleShipment(row)
    if (!shipment) return
    fulfillShipment.mutate({ orderId: row.id, shipmentId: shipment.id })
  }

  /**
   * §46 — every conditional item here is RECORD STATE (this order is already
   * fulfilled / has no assigned package / has no purchased label), so it HIDES
   * rather than rendering disabled. Details and Download are always available.
   */
  const getActionItems = useCallback(
    (row: ShopifyOrder) => [
      {
        label: 'Details',
        onClick: () => navigate(ROUTES.INTERNAL_ORDER(encodeURIComponent(String(row.id))), { state: referrer }),
        icon: <InfoIcon strokeWidth={1} />,
      },
      {
        // Singular / plural by the order's product count (one product = one box
        // to assign; several = several).
        label: (row.lineItems?.length ?? 0) > 1 ? 'Assign Packages' : 'Assign Package',
        onClick: () => startAssign([row]),
        visible: !row.fulfillmentStatus || row.fulfillmentStatus === 'unfulfilled',
        icon: <PackageIcon strokeWidth={1} />,
      },
      {
        // Single-product orders only — the box IS the product's own package,
        // so "assigned" is enough to buy a label without a separate "Create
        // shipment" step (that step is folded into the confirm handler).
        label: 'Buy Label',
        onClick: () => setBuyLabelOrder(row),
        visible: canBuyLabel(row),
        icon: <TagIcon strokeWidth={1} />,
      },
      {
        label: 'Fulfill',
        onClick: () => handleFulfill(row),
        visible: canFulfill(row),
        icon: <PackageCheckIcon strokeWidth={1} />,
      },
      {
        label: 'Print Shipping Label',
        onClick: () => printLabel(getSingleShipment(row)!.labelUrl!),
        visible: canPrintLabel(row),
        icon: <PrinterIcon strokeWidth={1} />,
      },
      {
        label: 'Download',
        onClick: () => handleDownloadInvoice(row),
        icon: <DownloadIcon strokeWidth={1} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, referrer],
  )

  /**
   * VERA-891: download the Vera order PDF for each selected order.
   *
   * Sequential, not parallel: each one triggers its own browser download, and
   * firing N at once gets throttled or blocked as a popup burst. A failure on
   * one order doesn't abort the rest — the run finishes and reports how many
   * failed, so selecting 20 and having one bad row doesn't cost the other 19.
   */
  const handleBulkDownload = async (rows: ShopifyOrder[]) => {
    if (!rows.length) return
    let failed = 0
    for (const row of rows) {
      try {
        await downloadOrderDocument.mutateAsync({ orderId: row.id, orderName: row.name })
      } catch {
        failed += 1
      }
    }
    if (failed > 0) {
      notify({
        type: 'error',
        text:
          failed === rows.length
            ? 'Unable to download these orders. Please try again.'
            : `${failed} of ${rows.length} orders could not be downloaded.`,
      })
    }
  }

  // Bulk actions on selected orders.
  const getBulkActions = useCallback(
    (selectedRows: ShopifyOrder[]) => [
      {
        label: 'Assign packages',
        onClick: () => startAssign(selectedRows),
        // Same glyph as the row kebab's "Assign Package" — one action, one icon.
        icon: <PackageIcon strokeWidth={1} />,
        visible: true,
      },
      {
        label: 'Download',
        onClick: () => handleBulkDownload(selectedRows),
        icon: <DownloadIcon strokeWidth={1} />,
        visible: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignQueue.length],
  )

  /**
   * The modals the actions above drive. Rendered by the consumer so each surface
   * keeps its own JSX tree; the state and handlers stay here so both surfaces
   * get identical behaviour.
   */
  const modals = (
    <>
      {/* Assign Packages — per-product scan-resolve, exactly like the Packages-
          page AddPackagesModal: a product preview above the reader, one package
          scanned per product. Kebab steps through one order's products; bulk
          steps through many orders (each with its products). The title carries
          the order 1/N (bulk) or product 1/N (single) badge; "Next Product" /
          "Next Order" advance. A successful assign auto-advances. */}
      <LinkPackageModal
        order={assignOrder}
        open={assignQueue.length > 0}
        onClose={closeAssign}
        refetch={refetch}
        scanMode
        singleSelect
        lineItemIds={assignProduct ? [assignProduct.id] : undefined}
        title={assignProducts.length > 1 ? 'Assign Packages' : 'Assign Package'}
        badge={
          isBulkAssign
            ? `${assignOrderIndex + 1}/${assignQueue.length}`
            : assignProducts.length > 1
              ? `${assignProductIndex + 1}/${assignProducts.length}`
              : undefined
        }
        expectedProduct={
          assignProduct
            ? { title: assignProduct.title, sku: assignProduct.variant?.sku }
            : undefined
        }
        productPreview={
          assignProduct ? (
            // size="large" (the default) is what draws the card border — Preview
            // only borders at "large", so parity with the Package card below
            // requires this, not "medium".
            <ProductPreview
              product={
                {
                  title: assignProduct.title,
                  sku: assignProduct.variant?.sku,
                  images: assignProduct.image ? [assignProduct.image] : [],
                } as any
              }
            />
          ) : null
        }
        onLinked={nextProduct}
        onBack={hasPrevProduct ? prevProduct : undefined}
        onNext={hasNextProduct ? nextProduct : hasNextOrder ? nextOrder : undefined}
        nextLabel={hasNextProduct ? 'Next Product' : 'Next Order'}
      />

      {/* Kebab "Buy Label" — single-product orders only. Money action, always
          confirmed first (reuses the same confirm modal as the Order Detail
          page's per-shipment Buy Label). */}
      <BuyLabelConfirmModal
        open={!!buyLabelOrder}
        onClose={closeBuyLabelConfirm}
        onConfirm={confirmBuyLabel}
        loading={createShipment.isPending || buyShipmentLabel.isPending}
        packageName={buyLabelOrder?.lineItems?.[0]?.title}
      />
    </>
  )

  return { getActionItems, getBulkActions, modals }
}
